import { ANEX, AnexApiClient } from '@anex/common'
import { makeAutoObservable } from 'mobx'
import { RootStore } from './RootStore'
import { TokenManager } from '~/utils/TokenManager'
import { IUsage } from '~/types'
import { AccountStore } from './AccountStore'
import { Dayjs } from 'dayjs'
import { BroadbandMonthlyUsageMock } from '~/utils/mock'

/**
 * Usage Store
 */
export class UsageStore {
  api: AnexApiClient
  account: AccountStore

  usageData: IUsage = {
    broadband: [
      {
        key: '2025-02-11+2025-03-11',
        data: BroadbandMonthlyUsageMock,
        type: 'monthly',
      },
    ],
    cdrs: [],
  }

  constructor(rootStore: RootStore) {
    this.api = rootStore.api
    this.account = rootStore.account
    makeAutoObservable(this)
  }

  get getUsage() {
    return this.usageData
  }

  async getUsageData(start: Dayjs, end: Dayjs) {
    const currentService = this.account.getCurrentService

    if (currentService && currentService.code) {
      if (currentService.type.toLowerCase() === 'voip') {
        await this.getVoipUsage(start, end)
        return this.usageData.cdrs
      } else {
        await this.getBroadbandMonthlyUsage(
          start.toISOString(),
          end.toISOString()
        )
        return (
          this.usageData.broadband.find(
            (u) => u.key === `${start.toISOString()}+${end.toISOString()}`
          )?.data || null
        )
      }
    }
  }

  /**
   * Get nbn/opticomm Broadband Monthly Usage
   * @param from date string format `YYYY-DD-MM`
   * @param to date string format `YYYY-DD-MM`
   */
  async getBroadbandMonthlyUsage(from: string, to: string) {
    const currentService = this.account.getCurrentService

    await this.api.V1.Account.Services.Broadband.Usage.MonthlyUsage(
      currentService?.code || '',
      from,
      to,
      TokenManager.getTokenHeader()
    )
      .then(
        (
          resp: ANEX.Website.API.Endpoints.V1.Account.Services.Broadband.Usage.MonthlyResponse
        ) => {
          const storeKey = `${from}+${to}`
          const itemIndex = this.usageData.broadband.findIndex(
            (k) => k.key === storeKey
          )
          if (itemIndex === -1) {
            this.usageData.broadband.push({
              key: storeKey,
              data: resp,
              type: 'monthly',
            })
          } else {
            this.usageData.broadband[itemIndex].data = resp
          }
        }
      )
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Get VoIP Usage
   * NOTE: on the response body, save only the `cdrs` object array in the state
   * @param from Dayjs object
   * @param to Dayjs object
   */
  async getVoipUsage(from: Dayjs, to: Dayjs) {
    const currentService = this.account.getCurrentService

    await this.api.V1.Account.Services.Voip.Usage(
      currentService?.code || '',
      from.format('YYYY-MM-DD'),
      to.format('YYYY-MM-DD'),
      false,
      TokenManager.getTokenHeader()
    )
      .then(
        (
          resp: ANEX.Website.API.Endpoints.V1.Account.Services.Voip.Usage.UsageResponse
        ) => {
          const storeKey = `${from.format('YYYY-MM-DD')}+${to.format('YYYY-MM-DD')}`
          const itemIndex = this.usageData.cdrs.findIndex(
            (k) => k.key === storeKey
          )
          if (itemIndex === -1) {
            this.usageData.cdrs.push({
              key: storeKey,
              data: resp.cdrs || [],
              type: 'cdrs',
            })
          } else {
            this.usageData.cdrs[itemIndex].data = resp.cdrs || []
          }
        }
      )
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }
}
